import { React, useState, useEffect, useRef } from 'react'
import { useMutation } from '@apollo/client'
import {
  CREATE_CAMPAIGN_QUERY,
  UPDATE_CAMPAIGN_QUERY,
} from '../../services/GraphQLQueries'
import '../../layouts/stylesheets/loader-style.css'
import { AiOutlineSnippets } from 'react-icons/ai'

const CampaignDestination = (props) => {
  const {
    campaignData,
    nav,
    oldCampaignData,
    pageType,
    setActivePage,
    setIsNavDisabled,
    setNav,
  } = props
  const [createCampaign, { loading }] = useMutation(CREATE_CAMPAIGN_QUERY)
  const [updateCampaign, { loadingUpdate }] = useMutation(UPDATE_CAMPAIGN_QUERY)
  const [campaignDestination, setCampaignDestination] = useState({
    domain: oldCampaignData?.websiteDomain ?? '',
    cartURL: oldCampaignData?.cartPageURL ?? '',
    landingURL: oldCampaignData?.landingPageURL ?? '',
    privacyURL: oldCampaignData?.privacyPageURL ?? '',
  })
  const [formErrors, setFormErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isSubmittingRef = useRef(false)
  const isLoading = loading || loadingUpdate || isSubmitting

  useEffect(() => {
    return () => {
      setIsNavDisabled?.(false)
    }
  }, [setIsNavDisabled])

  useEffect(() => {
    const handleNav = async () => {
      if (!nav) {
        return
      }

      const navDirection = nav
      setNav('')

      if (navDirection === 'back') {
        if (!isSubmittingRef.current) {
          setActivePage('campaignDetails')
        }
        return
      }

      if (navDirection !== 'forward' || isSubmittingRef.current) {
        return
      }

      if ((campaignDestination.landingURL || '').trim().length < 3) {
        setFormErrors((currentErrors) => ({
          ...currentErrors,
          landingURL: 'Please enter a landing page URL.',
        }))
        return
      }

      isSubmittingRef.current = true
      setIsSubmitting(true)
      setIsNavDisabled?.(true)

      try {
        const variables = {
          campaign: {
            ...campaignData,
            websiteDomain: campaignDestination.domain,
            cartPageURL: campaignDestination.cartURL,
            landingPageURL: campaignDestination.landingURL,
            privacyPageURL: campaignDestination.privacyURL,
          },
        }

        if (pageType === 'edit') {
          const { data } = await updateCampaign({
            variables: {
              ...variables,
              campaignId: oldCampaignData.id,
            },
          })

          if (data?.updateCampaign) {
            setActivePage('finalPage')
            return
          }
        } else {
          const { data } = await createCampaign({ variables })

          if (data?.createCampaign) {
            setActivePage('finalPage')
            return
          }

          setFormErrors((currentErrors) => ({
            ...currentErrors,
            submit: 'We could not save the campaign. Please try again.',
          }))
          return
        }

        setFormErrors((currentErrors) => ({
          ...currentErrors,
          submit: 'We could not save the campaign. Please try again.',
        }))
      } catch (error) {
        setFormErrors((currentErrors) => ({
          ...currentErrors,
          submit: 'We could not save the campaign. Please try again.',
        }))
      } finally {
        isSubmittingRef.current = false
        setIsSubmitting(false)
        setIsNavDisabled?.(false)
      }
    }

    handleNav()
  }, [
    campaignData,
    campaignDestination,
    createCampaign,
    nav,
    oldCampaignData,
    pageType,
    setActivePage,
    setIsNavDisabled,
    setNav,
    updateCampaign,
  ])

  const handleChange = (event) => {
    const { id, value } = event.target
    setCampaignDestination({
      ...campaignDestination,
      [id]: value,
    })
    setFormErrors((currentErrors) => ({
      ...currentErrors,
      [id === 'landingURL' ? 'landingURL' : id]: '',
      submit: '',
    }))
  }

  const rowClass =
    'grid w-full grid-cols-1 gap-2 md:grid-cols-[minmax(12rem,42%)_minmax(16rem,1fr)] md:gap-0'
  const labelClass =
    'flex h-12 items-center justify-center rounded-full bg-[#F7FAF7] px-5 text-center text-base font-unbounded text-gray-900 shadow-sm md:h-14 md:text-[17px] xl:h-16 xl:text-lg'
  const inputWrapClass = 'relative min-w-0 md:-ml-10'
  const inputClass =
    'h-12 w-full rounded-full border-0 bg-[#DEE6DD] px-6 text-center text-base text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#C9D7C2] md:h-14 md:px-8 md:text-[17px] xl:h-16 xl:text-lg'

  return isLoading ? (
    <>
      <div className="loader-wrapper">
        <div className="flex justify-center items-center">
          <div className="grid gap-10">
            <div className="loader"></div>
            <div className="text-center font-unbounded">
              {pageType === 'edit'
                ? 'Updating campaign...'
                : 'Creating campaign...'}
            </div>
          </div>
        </div>
      </div>
    </>
  ) : (
    <>
      <div className="w-full max-w-[80rem] px-4 sm:px-6">
        {/* Small heading */}
        <div className="flex items-center gap-2 mb-4 font-unbounded">
          <AiOutlineSnippets />

          <p className="uppercase">
            {pageType === 'edit' ? 'Update' : 'Add'} Campaign Destination
          </p>
        </div>

        {/* Big headline + subcopy */}
        <h3 className="text-2xl text-onTrack-nightRider mb-4 font-unbounded">
          Set up your M-Link
        </h3>
        <p className=" leading-snug font-light text-lg text-[#0b0b0b]/80 mb-10 max-w-[68rem] xl:mb-14">
          {
            'Provide your links that your audiences visit via online ads platforms, such as your main domain and "thank-you" pages.'
          }
        </p>

        {/* Row: Website domain */}
        <div className={`${rowClass} mb-5 xl:mb-6`}>
          <span className={labelClass}>
            Website domain
          </span>
          <div className={inputWrapClass}>
            <input
              id="domain"
              className={inputClass}
              placeholder="Website domain"
              required
              value={campaignDestination.domain}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Row: Add-to-Cart URL */}
        <div className={`${rowClass} mb-5 xl:mb-6`}>
          <span className={labelClass}>
            Add-to-Cart URL
          </span>
          <div className={inputWrapClass}>
            <input
              id="cartURL"
              className={inputClass}
              placeholder="Add to Cart Page URL"
              required
              value={campaignDestination.cartURL}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Row: Campaign Landing Page URL */}
        <div className={`${rowClass} mb-5 xl:mb-6`}>
          <span className={`${labelClass} flex-col`}>
            <span className="leading-none">Campaign Page</span>
            <span className="text-[11px] mt-1 opacity-70">
              (M-Key added here)
            </span>
          </span>
          <div className={inputWrapClass}>
            <input
              id="landingURL"
              className={inputClass}
              placeholder="Campaign Landing Page URL"
              required
              value={campaignDestination.landingURL}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>
        </div>
        {formErrors.landingURL ? (
          <p className="text-[#f44] font-bold text-center mb-4">
            {formErrors.landingURL}
          </p>
        ) : (
          ''
        )}
        {formErrors.submit ? (
          <p className="text-[#f44] font-bold text-center mb-4">
            {formErrors.submit}
          </p>
        ) : (
          ''
        )}

        {/* Optional: Privacy URL (kept for functionality, styled same) */}
        <div className={rowClass}>
          <span className={labelClass}>
            Privacy Page URL
          </span>
          <div className={inputWrapClass}>
            <input
              id="privacyURL"
              className={inputClass}
              placeholder="Privacy Page URL"
              required
              value={campaignDestination.privacyURL}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

    </>
  )
}

export default CampaignDestination
